import { classed } from '@tw-classed/react'
import { useProfileStore } from './ProfileStore.ts'
import { Icon } from '../../shared/ui/icon/icon.tsx'
import { cn } from '../../shared/lib/utils.ts'
import React, { useState, useEffect } from 'react'
import {
  Drawer,
  DrawerBar,
  DrawerClose,
  DrawerContent,
} from '../../shared/ui/drawer/drawer.tsx'
import { useNavigate } from 'react-router-dom'
import { useIsMobile } from '../../shared/lib/hooks.ts'
import { Dialog } from '../../shared/ui/dialog/dialog.tsx'

export const ProfileMenuBlock = classed.div(
  'divide-y divide-gray rounded-18 bg-gray-light',
)

export function ProfileButton(props: {
  imageUrl?: string
  title: React.ReactNode
  alertTitle?: boolean
  onClick: () => void
  subtitle?: React.ReactNode
  withArrow?: boolean
  disabled?: boolean
}) {
  return (
    <button
      className="flex h-80 w-full items-center gap-16 overflow-hidden p-16"
      onClick={props.onClick}
      disabled={props.disabled}
    >
      {props.imageUrl && <img src={props.imageUrl} />}
      <div className="min-w-0 flex-1 text-left">
        <div
          className={cn(
            'truncate text-18 font-bold',
            props.alertTitle && 'text-alert',
          )}
        >
          {props.title}
        </div>
        {props.subtitle && (
          <div className="text-14 font-semibold text-light">
            {props.subtitle}
          </div>
        )}
      </div>
      {props.withArrow && <Icon className="text-main" iconName="arrowRight" />}
    </button>
  )
}

export function ProfileTitle(props: {
  title: string | React.ReactNode
  hideBack?: boolean
}) {
  const navigate = useNavigate()
  return (
    <div className="relative mb-24 flex w-full justify-center gap-16">
      {!props.hideBack && (
        <button
          className="absolute left-16 shrink-0"
          onClick={() => {
            navigate(-1)
          }}
        >
          <Icon iconName="left" className="text-black" />
        </button>
      )}
      <h1
        className={cn(
          'text-18 font-bold text-black',
          props.hideBack && 'w-full text-center',
        )}
      >
        {props.title}
      </h1>
    </div>
  )
}

export function ProfileDrawer(props: {
  title: string | React.ReactNode
  children: React.ReactNode
  open: boolean
  gray?: boolean
}) {
  const store = useProfileStore()
  const isMobile = useIsMobile()
  const [isDialogOpen, setIsDialogOpen] = useState(props.open)

  useEffect(() => {
    setIsDialogOpen(props.open)
  }, [props.open])

  const handleChangeOpen = (open: boolean) => {
    if (!open) {
      setIsDialogOpen(open)
      setTimeout(() => {
        store.closeDrawer()
      }, 300)
    }
  }

  return isMobile ? (
    <Drawer
      open={props.open}
      onOpenChange={(open) => {
        if (!open) {
          store.closeDrawer()
        }
      }}
    >
      <DrawerContent
        direction="bottom"
        className={cn(
          'mx-auto mt-24 flex h-auto max-h-800 max-w-800 flex-col rounded-t-32',
          props.gray ? 'bg-light' : 'bg-white',
        )}
      >
        <DrawerBar />
        <div className="flex flex-col">
          <div className="flex items-center justify-between px-32 pt-16">
            <div className="text-20 font-bold">{props.title}</div>
            <DrawerClose asChild>
              <div className="flex size-32 items-center justify-center rounded-full bg-gray">
                <Icon iconName="close" className="text-black" />
              </div>
            </DrawerClose>
          </div>
          <div className="w-full px-32 py-16">{props.children}</div>
        </div>
      </DrawerContent>
    </Drawer>
  ) : (
    <Dialog.Root open={isDialogOpen} onOpenChange={handleChangeOpen}>
      <Dialog.Content className="max-w-[40vw]" bg="gray">
        <div className="flex flex-col">
          <div className="flex items-center justify-between">
            <div className="text-20 font-bold">{props.title}</div>
          </div>
          <div className="mt-24 w-full">{props.children}</div>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  )
}
